import {
  createShopApiGraphqlClient,
  type ShopApiGraphqlClient,
} from "../../../packages/pvc-product/shop-api-transport";

export type AccountArtworkFile = {
  id: string;
  createdAt: string;
  orderId: string;
  orderCode: string;
  orderLineId: string;
  originalFilename: string;
  mimeType: string;
  sizeBytes: number;
  version: number;
  downloadUrl: string;
};

const FILES_QUERY = `
  query HappyPrintMyArtworkFiles {
    myHappyPrintArtworkFiles {
      id
      createdAt
      orderId
      orderCode
      orderLineId
      originalFilename
      mimeType
      sizeBytes
      version
      downloadUrl
    }
  }
`;

const CREATE_UPLOAD_LINK_MUTATION = `
  mutation HappyPrintCreateMyArtworkUploadLink($orderLineId: ID!) {
    createMyHappyPrintArtworkUploadLink(orderLineId: $orderLineId) {
      orderLineId
      url
      expiresAt
    }
  }
`;

const REUSE_MUTATION = `
  mutation HappyPrintReuseMyArtwork($sourceFileId: ID!, $targetOrderLineId: ID!) {
    reuseMyHappyPrintArtwork(sourceFileId: $sourceFileId, targetOrderLineId: $targetOrderLineId) {
      id
      orderLineId
      originalFilename
      version
    }
  }
`;

function object(value: unknown, path: string): Record<string, unknown> {
  if (value == null || typeof value !== "object" || Array.isArray(value)) throw new Error(`${path} must be an object`);
  return value as Record<string, unknown>;
}

function string(value: unknown, path: string): string {
  if (typeof value !== "string" || !value.length) throw new Error(`${path} must be a non-empty string`);
  return value;
}

function integer(value: unknown, path: string): number {
  if (typeof value !== "number" || !Number.isInteger(value)) throw new Error(`${path} must be an integer`);
  return value;
}

function decodeFile(value: unknown, path: string): AccountArtworkFile {
  const file = object(value, path);
  return {
    id: string(file.id, `${path}.id`),
    createdAt: string(file.createdAt, `${path}.createdAt`),
    orderId: string(file.orderId, `${path}.orderId`),
    orderCode: string(file.orderCode, `${path}.orderCode`),
    orderLineId: string(file.orderLineId, `${path}.orderLineId`),
    originalFilename: string(file.originalFilename, `${path}.originalFilename`),
    mimeType: string(file.mimeType, `${path}.mimeType`),
    sizeBytes: integer(file.sizeBytes, `${path}.sizeBytes`),
    version: integer(file.version, `${path}.version`),
    downloadUrl: string(file.downloadUrl, `${path}.downloadUrl`),
  };
}

export function createAccountFilesApi(client: ShopApiGraphqlClient) {
  return {
    async load(signal?: AbortSignal): Promise<AccountArtworkFile[]> {
      const raw = await client.execute({ operationName: "HappyPrintMyArtworkFiles", document: FILES_QUERY, signal });
      const data = object(raw, "data");
      if (!Array.isArray(data.myHappyPrintArtworkFiles)) throw new Error("data.myHappyPrintArtworkFiles must be an array");
      return data.myHappyPrintArtworkFiles.map((item, index) => decodeFile(item, `data.myHappyPrintArtworkFiles[${index}]`));
    },

    async createUploadLink(orderLineId: string): Promise<{ orderLineId: string; url: string; expiresAt: string }> {
      const raw = await client.execute({
        operationName: "HappyPrintCreateMyArtworkUploadLink",
        document: CREATE_UPLOAD_LINK_MUTATION,
        variables: { orderLineId },
      });
      const data = object(raw, "data");
      const link = object(
        data.createMyHappyPrintArtworkUploadLink,
        "data.createMyHappyPrintArtworkUploadLink",
      );
      return {
        orderLineId: string(
          link.orderLineId,
          "data.createMyHappyPrintArtworkUploadLink.orderLineId",
        ),
        url: string(link.url, "data.createMyHappyPrintArtworkUploadLink.url"),
        expiresAt: string(
          link.expiresAt,
          "data.createMyHappyPrintArtworkUploadLink.expiresAt",
        ),
      };
    },

    async reuse(sourceFileId: string, targetOrderLineId: string): Promise<void> {
      const raw = await client.execute({
        operationName: "HappyPrintReuseMyArtwork",
        document: REUSE_MUTATION,
        variables: { sourceFileId, targetOrderLineId },
      });
      const data = object(raw, "data");
      object(data.reuseMyHappyPrintArtwork, "data.reuseMyHappyPrintArtwork");
    },
  };
}

export function createBrowserAccountFilesApi() {
  const endpoint = import.meta.env.VITE_SHOP_API_URL || "/shop-api";
  return createAccountFilesApi(createShopApiGraphqlClient({ endpoint }));
}
